import { useEffect, useMemo, useRef, useState } from "react";

export interface SelectOption {
    value: string;
    label: string;
}

interface SearchableSelectProps {
    id?: string;
    value: string;
    options: SelectOption[];
    onChange: (value: string) => void;
    placeholder?: string;
    required?: boolean;
    invalid?: boolean;
}

/**
 * A typeahead single-select over a list of options. There was no searchable
 * select in the app; the onboarding admin fields and the direct-manager picker
 * need one. Controlled by `value` (the option's value); the input shows the
 * matching label and filters as you type.
 */
export function SearchableSelect({
    id, value, options, onChange, placeholder, required, invalid,
}: SearchableSelectProps) {
    const [open, setOpen] = useState(false);
    const [query, setQuery] = useState("");
    const [active, setActive] = useState(0);
    const wrapRef = useRef<HTMLDivElement>(null);

    const selectedLabel = useMemo(
        () => options.find((o) => o.value === value)?.label ?? "",
        [options, value]
    );

    const filtered = useMemo(() => {
        const q = query.trim().toLowerCase();
        if (!q) return options;
        return options.filter((o) => o.label.toLowerCase().includes(q));
    }, [options, query]);

    // Close on outside click.
    useEffect(() => {
        if (!open) return;
        const onDoc = (e: MouseEvent) => {
            if (wrapRef.current && !wrapRef.current.contains(e.target as Node)) setOpen(false);
        };
        document.addEventListener("mousedown", onDoc);
        return () => document.removeEventListener("mousedown", onDoc);
    }, [open]);

    const choose = (v: string) => {
        onChange(v);
        setQuery("");
        setOpen(false);
    };

    return (
        <div className="search-select" ref={wrapRef}>
            <input
                id={id}
                type="text"
                role="combobox"
                aria-expanded={open}
                aria-invalid={invalid}
                autoComplete="off"
                placeholder={placeholder ?? "Search…"}
                value={open ? query : selectedLabel}
                onFocus={() => { setOpen(true); setActive(0); }}
                onChange={(e) => { setQuery(e.target.value); setOpen(true); setActive(0); }}
                onKeyDown={(e) => {
                    if (e.key === "ArrowDown") { e.preventDefault(); setOpen(true); setActive((a) => Math.min(a + 1, filtered.length - 1)); }
                    else if (e.key === "ArrowUp") { e.preventDefault(); setActive((a) => Math.max(a - 1, 0)); }
                    else if (e.key === "Enter" && open) { e.preventDefault(); const o = filtered[active]; if (o) choose(o.value); }
                    else if (e.key === "Escape") { setOpen(false); }
                }}
            />
            {!required && value && !open && (
                <button type="button" className="search-select-clear" aria-label="Clear" onClick={() => choose("")}>✕</button>
            )}
            {open && (
                <ul className="search-select-menu" role="listbox">
                    {filtered.length === 0 ? (
                        <li className="search-select-empty">No matches</li>
                    ) : (
                        filtered.map((o, i) => (
                            <li
                                key={o.value}
                                role="option"
                                aria-selected={o.value === value}
                                className={`search-select-option ${i === active ? "active" : ""} ${o.value === value ? "selected" : ""}`}
                                onMouseEnter={() => setActive(i)}
                                onMouseDown={(e) => { e.preventDefault(); choose(o.value); }}
                            >
                                {o.label}
                            </li>
                        ))
                    )}
                </ul>
            )}
        </div>
    );
}
